import { CodeInfo, TaskLinkEntity } from './task.props.type';
import { LabelEntity } from '../shared/label';
import { Shareable } from '../shared/activity.sharing';
import { TaskEvidenceResponses } from './task.evidence';
import { TaskGroupBy, TaskLinkType, TaskStatus } from './task.enum';
import { TaskKind } from './task.kind';
import { WithConnectStamp } from '../connect/connect.stamp';
import { ContactEntity } from '../contact/contact.options';
import { Entity, AuditDetails } from '../entity';
import { TaskTemplate } from './task.template';
import { ScheduleRule } from '../../scheduling/schedule.rule';
import { TaskValidation } from './task.validation';
import { EntityStatusStamp } from '../entity.status/entity.status.stamp';
import { TaskEvidenceException } from './task.evidence.exception';
import { TaskEvidenceOutcome } from './task.evidence.outcome';

export interface Task extends TaskTemplate, TaskEvidenceResponses, AuditDetails, WithConnectStamp {

    /**
     * The legacy Open / Completed mirror of `taskStatus`, written only by the stamp helper.
     *
     * Kept because 47 server paths, the seeded open-tasks report view, tenants' saved reports and the
     * field app read it. New code reads `taskStatus` (DP-4, amended 21 September).
     */
    status?: TaskStatus;

    /** The status the task holds, with the reason it was given. */
    taskStatus?: EntityStatusStamp;

    assignee?: ContactEntity;
    description?: string;
    dueDate?: Date;
    daily?: boolean;
    acknowledgement?: boolean;
    instance?: boolean;

    completedBy?: Entity;
    completedOn?: Date;

    data?: any | CodeInfo;

    /** Use like tags, to know where this task linked to (Eg: Code, WorkOrder, ...) */
    links?: TaskLinkEntity[];

    virtual?: boolean;
    systemGenerated?: boolean;

    /** This is used to determine if the tasks was added or modified */
    batchId?: string;

    // Flag to track old compliance
    legacy?: boolean;

    /** use to return the daily tasks */
    instances?: Entity[];

    // this will combine to the tags
    phaseTag?: LabelEntity;

    effectiveSharing?: Shareable;

    // This is used to determine the task is generated when adding claim items
    generatedFromClaim?: boolean;

    // Schedule for recurring tasks
    schedule?: ScheduleRule;

    validation?: TaskValidation;

    /** Server owned: written only by the record and remove commands, cleared when nothing it explains is missing. */
    evidenceException?: TaskEvidenceException;

    /** Server owned: derived from status, evidence and exception on every write that changes them; undefined until a task is first touched (no backfill). */
    evidenceOutcome?: TaskEvidenceOutcome;

    /** API-derived direct-child completion shown on parent task rows. */
    childProgress?: TaskChildProgress;

    // This for the job/Stage is close/canceled/Finalised
    isReadonly?: boolean;
}

export interface TaskChildProgress {
    completed: number;
    total: number;
}

export interface NestedTask extends Task {
    children: NestedTask[];
    virtualParent: NestedTask;
    groupLinks: Record<TaskLinkType, TaskLinkEntity>;
    fullParent: NestedTask;
    hasComplianceSubtask: boolean;
    currentGroupBy: TaskGroupBy
}

export function isTaskAnswered(task: TaskEvidenceResponses) {

    if (!!task?.fileResponse?.length)
        return true;

    if (!!task?.formResponse?.length)
        return true;

    if (!!task?.formAttachments?.length)
        return true;

    if (!!task?.additionalFiles?.length)
        return true;

    if (!!task?.afterPhotos?.length)
        return true;

    if (!!task?.beforePhotos?.length)
        return true;

    if (!!task?.duringPhotos?.length)
        return true;

    if (!!task?.signature?.length)
        return true;

    if (!!task?.signatureAttachments?.length)
        return true;

    if (!!task?.comment?.length)
        return true;

    return false;
}

export enum TaskSystemViewVariant {
    Job = 'Job',
    SOR = 'SOR',
}
